import * as React from "react";
import { cn } from "@app/ui/lib/Utils";
import { Input } from "@app/ui/atom/Input";

export interface AuthFieldProps
  extends Omit<React.ComponentProps<typeof Input>, "value" | "onChange"> {
  /** 입력 위에 붙는 라벨. 번역은 호출부(각 필드 컴포넌트)가 책임진다. */
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  /** 검증 실패 문구. 이미 번역된 상태로 받는다(useAuthErrorText 참고). */
  error?: string;
  /**
   * true 면 에러 문구를 입력 아래에 텍스트로 깔지 않는다 — 빨간 테두리만 남긴다.
   * 문구는 leftSlot 의 툴팁 등으로 대신 보여줄 때 쓴다(예: 비밀번호 필드).
   */
  hideErrorText?: boolean;
  /** 입력 왼쪽 슬롯(툴팁 아이콘 등). Input 으로 그대로 넘긴다. */
  leftSlot?: React.ReactNode;
  /** 입력 오른쪽 슬롯. Input 으로 그대로 넘긴다. */
  rightSlot?: React.ReactNode;
  /** 바깥 래퍼(label + input + error) 에 붙는 클래스. */
  className?: string;
}

/**
 * 로그인/가입/비밀번호 화면의 입력 한 칸. 라벨·입력·에러 문구 배치를 한 곳에 모아
 * EmailField/NicknameField/PasswordField/PasswordConfirmField 가 공유한다.
 */
const AuthField = React.forwardRef<HTMLInputElement, AuthFieldProps>(
  (
    { label, value, onChange, error, hideErrorText, leftSlot, rightSlot, id, className, ...inputProps },
    ref,
  ) => {
    return (
      <div className={cn("flex flex-col gap-1", className)}>
        <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
          {label}
        </label>
        <Input
          ref={ref}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={error ? true : undefined}
          leftSlot={leftSlot}
          rightSlot={rightSlot}
          className={cn(
            "border-0 bg-muted shadow-none",
            error && "ring-1 ring-destructive",
          )}
          {...inputProps}
        />
        {error && !hideErrorText && <p className="text-sm text-destructive">{error}</p>}
      </div>
    );
  },
);
AuthField.displayName = "AuthField";

export { AuthField };
